import irmHeader from 'assets/images/irm-header.png';

const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const fmtExpiry = (v) => {
  if (!v) return '—';
  const [yr, mo] = String(v).split('-');
  return (yr && mo) ? `${MONTHS[parseInt(mo, 10) - 1]} ${yr}` : v;
};
const fmtDate = (d) => {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};
const fmtNum = (v, dec = 0) => Number(v || 0).toLocaleString('en-IN', { minimumFractionDigits: dec, maximumFractionDigits: dec });

const letterheadHtml = (PRIMARY, title, ph) => `
  <div style="margin-bottom:6px">
    <div style="line-height:0">
      <img src="${irmHeader}" alt="Company Header" style="width:100%;object-fit:contain;display:block">
    </div>
    <div style="text-align:center;margin-top:5px">
      <div style="font-size:13pt;font-weight:700;letter-spacing:1.2px;font-family:'Montserrat',Arial,sans-serif;color:${PRIMARY}">${title}</div>
    </div>
  </div>
  <table style="width:100%;border-collapse:collapse;border:1.5px solid ${'#b2d8de'}">
    <tr>
      <td style="padding:5px 8px">
        <div style="font-size:11pt;font-weight:900;color:${PRIMARY};line-height:1.1">${ph?.name || 'PHARMACY'}</div>
        ${ph?.sub_title ? `<div style="font-size:7.5pt;color:#444;margin-top:1px">${ph.sub_title}</div>` : ''}
        <div style="font-size:7pt;color:#333;margin-top:2px;line-height:1.5">${ph?.address || ''}${ph?.city ? ', '+ph.city : ''}${ph?.pincode ? ' - '+ph.pincode : ''}</div>
        ${ph?.dl_no ? `<div style="font-size:6.5pt;color:#555">DL No : ${ph.dl_no}</div>` : ''}
      </td>
    </tr>
  </table>`;

const printedByFooter = (PRIMARY, BORDER) => `
    <div style="margin-top:8px;display:flex;justify-content:flex-end;align-items:center;font-size:7pt;color:#888;border-top:1px solid ${BORDER};padding-top:4px">
      <span>Printed on: ${new Date().toLocaleString('en-IN')}</span>
    </div>`;

const openPrintWindow = (title, toolbarLabel, bodyHtml, opts = {}) => {
  const win = window.open('', '_blank', 'width=1000,height=720');
  const toolbar = `<div id="ptb" style="position:fixed;top:0;left:0;right:0;background:#00798E;color:#fff;padding:9px 16px;display:flex;justify-content:space-between;align-items:center;z-index:9999;font-family:Arial,sans-serif;box-shadow:0 2px 8px rgba(0,0,0,0.25)"><span style="font-weight:700;font-size:10.5pt">${toolbarLabel}</span><div style="display:flex;gap:8px"><button onclick="window.print()" style="background:#fff;color:#00798E;border:none;padding:7px 16px;border-radius:4px;font-weight:700;cursor:pointer;font-size:9pt">&#128438; Print</button><button onclick="window.close()" style="background:transparent;color:rgba(255,255,255,0.8);border:1px solid rgba(255,255,255,0.4);padding:7px 10px;border-radius:4px;font-weight:700;cursor:pointer;font-size:9pt">&#10005;</button></div></div>`;
  const content = opts.fitToOnePage
    ? `<div class="pf-page" style="width:484px;margin:0 auto;padding:6px">${bodyHtml}</div>
       <script>window.onload=function(){try{var els=document.querySelectorAll('.pf-page');for(var i=0;i<els.length;i++){var h=els[i].scrollHeight;if(h>718)els[i].style.zoom=Math.max(0.55,718/h);}}catch(e){}};</script>`
    : `<div style="padding:6px">${bodyHtml}</div>`;
  win.document.write(`<!DOCTYPE html><html><head><title>${title}</title>
    <link rel="stylesheet" href="https://fonts.googleapis.com/css2?family=Montserrat:wght@400;700;900&display=swap">
    <style>@page{size:${opts.pageSize || 'A4 landscape'};margin:10mm}*{box-sizing:border-box}body{font-family:'Montserrat',Arial,sans-serif;margin:0;padding:50px 0 0 0;-webkit-print-color-adjust:exact;print-color-adjust:exact}td,th{overflow-wrap:break-word}@media print{#ptb{display:none!important}body{padding-top:0!important}}</style>
    </head><body>${toolbar}${content}</body></html>`);
  win.document.close();
};

/**
 * Print a Stock Adjustment Voucher — branded to match the rest of the pharmacy
 * print family (irmHeader logo, Montserrat title, #b2d8de bordered letterhead).
 * target : the inventory row being adjusted (item/variant/batch/expiry context)
 * adj    : { qty_delta, notes, balance_after } — the adjustment just applied
 * ph     : selected pharmacy object
 */
export function printAdjustmentVoucher(adj, target, ph) {
  if (!adj || !target) return;
  const PRIMARY = '#00798E';
  const BORDER = '#b2d8de';
  const LIGHT_BG = '#e0f4f7';
  const C = `border:1px solid ${BORDER};padding:4px 7px;font-size:8pt;`;
  const isIncrease = Number(adj.qty_delta) >= 0;
  const name = target.item?.name || '—';
  const variant = target.variant?.variant_name ? ` (${target.variant.variant_name})` : '';

  const body = `
    ${letterheadHtml(PRIMARY, 'STOCK ADJUSTMENT VOUCHER', ph)}
    <table style="width:100%;border-collapse:collapse;border:1px solid ${BORDER};border-top:none;margin-top:0;font-size:8pt">
      <tr>
        <td style="${C}width:20%;color:#777">Date</td>
        <td style="${C}font-weight:700">${fmtDate(new Date())}</td>
        <td style="${C}width:20%;color:#777">Location</td>
        <td style="${C}font-weight:700">${target.location?.name || '—'}</td>
      </tr>
      <tr>
        <td style="${C}color:#777">Item</td>
        <td style="${C}font-weight:700" colspan="3">${name}${variant}</td>
      </tr>
      <tr>
        <td style="${C}color:#777">Batch</td>
        <td style="${C}font-family:monospace">${target.batch_no || '—'}</td>
        <td style="${C}color:#777">Expiry</td>
        <td style="${C}font-family:monospace">${fmtExpiry(target.expiry_date)}</td>
      </tr>
    </table>
    <table style="width:100%;border-collapse:collapse;border:1px solid ${BORDER};border-top:none;margin-top:0;font-size:8pt">
      <tr style="background:${LIGHT_BG}">
        <th style="${C}text-align:left;color:${PRIMARY}">Adjustment</th>
        <th style="${C}text-align:right;color:${PRIMARY}">Qty</th>
      </tr>
      <tr>
        <td style="${C}">${isIncrease ? 'Stock Added' : 'Stock Removed'}</td>
        <td style="${C}text-align:right;font-family:monospace;font-weight:800;font-size:11pt;color:${isIncrease ? '#2e7d32' : '#c62828'}">${isIncrease ? '+' : ''}${fmtNum(adj.qty_delta, 3)}</td>
      </tr>
      <tr>
        <td style="${C}">Balance After Adjustment</td>
        <td style="${C}text-align:right;font-family:monospace;font-weight:700">${fmtNum(adj.balance_after, 3)}</td>
      </tr>
    </table>
    <table style="width:100%;border-collapse:collapse;border:1px solid ${BORDER};border-top:none;font-size:8pt">
      <tr>
        <td style="${C}color:#777;white-space:nowrap;vertical-align:top">Reason</td>
        <td style="${C}">${adj.notes || '—'}</td>
      </tr>
    </table>
    <div style="margin-top:24px;display:flex;justify-content:flex-end;font-size:8pt;color:#555">
      <div style="text-align:center;min-width:160px">
        <div style="border-top:1px solid #555;padding-top:4px">Authorised Signatory</div>
      </div>
    </div>
    ${printedByFooter(PRIMARY, BORDER)}
  `;
  openPrintWindow('Stock Adjustment Voucher', `Stock Adjustment — ${name}`, body, { pageSize: 'A5 portrait', fitToOnePage: true });
}

/**
 * Print a tabular stock report (Current / Low / Expiring) — same branded family.
 * rows  : array of inventory rows (same shape as inventory/index.js's `displayStock`)
 * title : report title, e.g. "Current Stock Report"
 * ph    : selected pharmacy object
 */
export function printStockReport(rows, title, ph) {
  if (!rows) return;
  const PRIMARY = '#00798E';
  const BORDER = '#b2d8de';
  const LIGHT_BG = '#e0f4f7';
  const C = `border:1px solid ${BORDER};padding:3px 6px;font-size:7.5pt;`;
  const CH = `${C}background:${LIGHT_BG};color:${PRIMARY};font-weight:700;text-align:center;`;

  const bodyRows = rows.map((s, i) => `
    <tr style="${i % 2 === 0 ? '' : `background:${LIGHT_BG}66`}">
      <td style="${C}text-align:center">${i + 1}</td>
      <td style="${C}"><span style="font-weight:600">${s.item?.name || '—'}</span>${s.variant?.variant_name ? ` <small style="color:#777">(${s.variant.variant_name})</small>` : ''}</td>
      <td style="${C}font-family:monospace;text-align:center">${s.batch_no || '—'}</td>
      <td style="${C}text-align:center">${fmtExpiry(s.expiry_date)}</td>
      <td style="${C}">${s.location?.name || '—'}</td>
      <td style="${C}text-align:right;font-family:monospace;font-weight:700">${fmtNum(s.qty_base_units, 2)}</td>
      <td style="${C}text-align:right;font-family:monospace">${fmtNum(s.mrp, 2)}</td>
      <td style="${C}text-align:right;font-family:monospace">${fmtNum(s.purchase_price, 2)}</td>
    </tr>`).join('');

  const totalQty = rows.reduce((s, r) => s + Number(r.qty_base_units || 0), 0);
  const totalValue = rows.reduce((s, r) => s + Number(r.qty_base_units || 0) * Number(r.purchase_price || 0), 0);

  const body = `
    ${letterheadHtml(PRIMARY, title.toUpperCase(), ph)}
    <div style="margin:4px 0;font-size:7.5pt;color:#555">Generated: ${fmtDate(new Date())} &nbsp;|&nbsp; ${rows.length} record(s)</div>
    <table style="width:100%;border-collapse:collapse;border:1px solid ${BORDER};border-top:none;table-layout:fixed">
      <tr>
        <th style="${CH}width:4%">#</th>
        <th style="${CH}text-align:left">Item</th>
        <th style="${CH}width:12%">Batch</th>
        <th style="${CH}width:10%">Expiry</th>
        <th style="${CH}width:14%">Location</th>
        <th style="${CH}width:9%">Stock</th>
        <th style="${CH}width:9%">MRP</th>
        <th style="${CH}width:9%">Purch. Price</th>
      </tr>
      ${bodyRows}
      <tr style="background:${LIGHT_BG}">
        <td colspan="5" style="${C}text-align:right;font-weight:700">Totals</td>
        <td style="${C}text-align:right;font-family:monospace;font-weight:800">${fmtNum(totalQty, 2)}</td>
        <td colspan="2" style="${C}text-align:right;font-family:monospace;font-weight:800">Value: ₹${fmtNum(totalValue, 2)}</td>
      </tr>
    </table>
    ${printedByFooter(PRIMARY, BORDER)}
  `;
  openPrintWindow(title, title, body);
}
